import { Button } from 'antd/lib'
import { CircleLoader } from 'shared/components/CircleLoader/CircleLoader'
import { PanelHeader } from 'Components/common/TaskPanelHeader/TaskPanelHeader'
import { useCallback, useEffect, useState } from 'react'
import CustomValidationIcon from 'Assets/icons/custom-validation.svg'
import classes from './CustomValidation.module.less'

export type ValidationResult = {
    result: 'pass' | 'fail' | 'error'
    messages: string[]
}

export type CustomValidationProps = {
    runValidationCheck: () => Promise<void>
    isInitialized: () => boolean
    getValidationResult: () => ValidationResult | null
}

export const CustomValidation = (props: CustomValidationProps) => {
    const [isValidating, setIsValidating] = useState(false)
    const [mode, setMode] = useState<'validating' | 'hint' | 'initializing' | 'result'>(
        'initializing'
    )

    const validationResult = props.getValidationResult()

    useEffect(() => {
        if (props.isInitialized() && !isValidating && !validationResult) {
            setMode('hint')
        }

        if (validationResult) {
            setMode('result')
        }
    }, [props, props.isInitialized, isValidating, validationResult])

    const handleRunValidationCheck = useCallback(async () => {
        setIsValidating(true)
        setMode('validating')
        await props.runValidationCheck()
        setIsValidating(false)
    }, [props])

    return (
        <div className={classes.customValidationRoot}>
            <PanelHeader logo={CustomValidationIcon} title={'Custom Validation'} />
            <div className={classes.body}>
                {(() => {
                    switch (mode) {
                        case 'validating':
                            return (
                                <div className={classes.loadingContainer}>
                                    <CircleLoader size={28} />
                                    <div>Validating...Please wait...</div>
                                </div>
                            )
                        case 'initializing':
                            return (
                                <div className={classes.loadingContainer}>
                                    <CircleLoader size={28} />
                                    <div>Loading...Please wait...</div>
                                </div>
                            )
                        case 'hint':
                            return (
                                <div className={classes.hintText}>
                                    Click on "Run Validation Check" to check your response.
                                </div>
                            )
                        case 'result':
                            return validationResult ? (
                                <div className={classes.resultContainer}>
                                    <div className={`${classes.resultStatus}`}>
                                        Result:{' '}
                                        <span className={`${classes[validationResult.result]}`}>
                                            {validationResult.result.toUpperCase()}
                                        </span>
                                    </div>
                                    {validationResult.result === 'fail' && (
                                        <div className={classes.errorMessage}>
                                            Some checks did not pass. Please review your response.
                                        </div>
                                    )}
                                    {validationResult.messages.length > 1 ? (
                                        <ul className={classes.resultMessages}>
                                            {validationResult.messages.map((message, index) => (
                                                <li key={index} className={classes.resultMessage}>
                                                    {message}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className={classes.resultMessage}>
                                            {validationResult.messages[0]}
                                        </div>
                                    )}
                                </div>
                            ) : null
                        default:
                            return null
                    }
                })()}
            </div>
            <div className={classes.footer}>
                <Button
                    className={classes.runButton}
                    onClick={handleRunValidationCheck}
                    disabled={isValidating || !props.isInitialized()}
                >
                    {validationResult ? 'Run Validation Check Again' : 'Run Validation Check'}
                </Button>
            </div>
        </div>
    )
}
